import { ArrowUpRight } from 'lucide-react';
import { Link } from '@/components/LocalizedLink';
import {
  TRUST_COPY,
  getDeclaredTrustFacts,
  getDeclaredTrustStatements,
  getTrustFacts,
  getTrustObservation,
  type TrustLang,
} from '@/lib/trustFacts';

type Props = {
  lang: TrustLang;
  className?: string;
  /** Heading level; pages that already use h2 for sections keep the default. */
  headingLevel?: 'h2' | 'h3';
};

const linkClass = 'text-foreground underline underline-offset-4 hover:text-muted-foreground transition-colors';

const TrustFactsBlock = ({ lang, className = '', headingLevel = 'h2' }: Props) => {
  const facts = getTrustFacts(lang);
  const declaredFacts = getDeclaredTrustFacts(lang);
  const declaredStatements = getDeclaredTrustStatements(lang);
  const observation = getTrustObservation(lang);
  const Heading = headingLevel;

  return (
    <section
      data-testid="trust-facts"
      aria-labelledby="trust-facts-title"
      className={`rounded-2xl border border-border/70 p-6 lg:p-8 bg-card/40 ${className}`}
    >
      <Heading id="trust-facts-title" className="font-display text-2xl lg:text-3xl text-foreground mb-2">
        {TRUST_COPY.title[lang]}
      </Heading>
      <p className="font-body text-sm text-muted-foreground mb-6 max-w-3xl">{TRUST_COPY.intro[lang]}</p>
      <dl className="grid grid-cols-2 md:grid-cols-3 gap-px overflow-hidden rounded-xl border border-border/60 bg-border/60">
        {facts.map((fact) => (
          <div key={fact.id} data-trust-fact={fact.id} className="bg-background p-4 lg:p-5">
            <dt className="font-body text-[11px] tracking-[0.15em] uppercase text-muted-foreground mb-1">{fact.label}</dt>
            <dd className="font-display text-2xl lg:text-3xl font-bold text-foreground">
              {fact.href ? (
                <Link to={fact.href} className="hover:text-muted-foreground transition-colors">
                  {fact.value}
                </Link>
              ) : (
                fact.value
              )}
            </dd>
            {fact.detail ? (
              <dd className="font-body text-xs text-muted-foreground leading-relaxed mt-1">{fact.detail}</dd>
            ) : null}
          </div>
        ))}
      </dl>
      <div data-trust-source="company-declared" className="mt-6">
        <p className="font-body text-[11px] tracking-[0.15em] uppercase text-muted-foreground mb-3">
          {TRUST_COPY.declaredCaption[lang]}
        </p>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-px overflow-hidden rounded-xl border border-border/60 bg-border/60">
          {declaredFacts.map((fact) => (
            <div key={fact.id} data-trust-fact={fact.id} className="bg-background p-4 lg:p-5">
              <dt className="font-body text-[11px] tracking-[0.15em] uppercase text-muted-foreground mb-1">{fact.label}</dt>
              <dd className="font-display text-2xl lg:text-3xl font-bold text-foreground">{fact.value}</dd>
              {fact.detail ? (
                <dd className="font-body text-xs text-muted-foreground leading-relaxed mt-1">{fact.detail}</dd>
              ) : null}
            </div>
          ))}
        </dl>
        <dl className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {declaredStatements.map((statement) => (
            <div key={statement.id} data-trust-fact={statement.id} className={statement.groups ? 'md:col-span-2' : undefined}>
              <dt className="font-body text-[11px] tracking-[0.15em] uppercase text-muted-foreground mb-1">{statement.label}</dt>
              <dd className="font-body text-sm text-foreground leading-relaxed">
                {statement.groups ? (
                  <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2">
                    {statement.groups.map((group) => (
                      <li key={group.id} data-tech-group={group.id}>
                        <span className="text-muted-foreground">{group.label}:</span> {group.items.join(', ')}
                      </li>
                    ))}
                  </ul>
                ) : (
                  statement.text
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      {observation ? (
        <div data-trust-fact="observation" className="mt-6 max-w-3xl">
          <p className="font-body text-sm text-foreground">
            <Link to={observation.href} className={linkClass}>
              {observation.value}
            </Link>{' '}
            <span className="text-muted-foreground">— {observation.label}</span>
          </p>
          <p className="font-body text-xs text-muted-foreground mt-1">{observation.source}</p>
        </div>
      ) : null}
      <p className="font-body text-sm mt-4">
        <a href={TRUST_COPY.clutchUrl} target="_blank" rel="noopener noreferrer" className={`${linkClass} inline-flex items-center gap-1`}>
          {TRUST_COPY.clutch[lang]}
          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
        </a>
      </p>
    </section>
  );
};

export default TrustFactsBlock;
